import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtMXN } from "@/lib/format";
import { LogOut, Package, Repeat, Users, Heart } from "lucide-react";

export default function LoginPage() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "", referral_code: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [referrerName, setReferrerName] = useState("");
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (user) {
      if (user.role === "admin") navigate("/admin");
      else navigate("/cuenta");
    }
  }, [user, loading, navigate]);

  // Pre-fill referral_code from ?ref=COMPARE-XXXXX
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      setForm((f) => ({ ...f, referral_code: ref.toUpperCase() }));
      setMode("register");
      // validate to show name
      api.get(`/referrals/validate/${ref.toUpperCase()}`)
        .then((r) => setReferrerName(r.data.referrer_first_name))
        .catch(() => {});
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      let u;
      if (mode === "login") {
        u = await login(form.email, form.password);
      } else {
        if (!form.name) {
          setErr("Ingresa tu nombre.");
          setLoading(false);
          return;
        }
        u = await register(form);
      }
      // Redirect based on role + optional ?redirect param
      const params = new URLSearchParams(window.location.search);
      const target = params.get("redirect");
      if (target) {
        navigate(target);
      } else if (u?.role === "admin") {
        navigate("/admin");
      } else {
        navigate("/cuenta");
      }
    } catch (e) {
      setErr(e.response?.data?.detail || "Error al procesar. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-arre-black min-h-screen flex items-center justify-center py-20 px-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">⟡ Mi cuenta</div>
        <h1 className="font-display text-5xl tracking-wider uppercase text-white leading-none mb-3">
          {mode === "login" ? "Hola compare" : "Únete, compare"}
        </h1>
        <p className="text-white/60 text-sm mb-8">
          {mode === "login" ? "Ingresa para ver tus pedidos." : "Crea tu cuenta en 30 segundos."}
        </p>

        <form onSubmit={submit} className="space-y-4 bg-arre-surface border border-arre-border p-6">
          {mode === "register" && (
            <>
              {referrerName && (
                <div className="p-3 border border-arre-red/40 bg-arre-red/10 text-sm text-white" data-testid="referral-banner">
                  ¡Qué onda compare! Te invita <strong>{referrerName}</strong>. Al registrarte y hacer tu primera compra ambos se llevan saldo.
                </div>
              )}
              <Input label="Nombre completo" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="auth-name" />
              <Input label="WhatsApp (opcional)" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="auth-phone" />
              <Input label="Código de referido (opcional)" value={form.referral_code} onChange={(v) => setForm({ ...form, referral_code: v.toUpperCase() })} testid="auth-referral" />
            </>
          )}
          <Input label="Correo" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="auth-email" />
          <Input label="Contraseña" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="auth-password" />
          {err && <p className="text-arre-red text-sm" data-testid="auth-error">{err}</p>}
          <button
            type="submit"
            disabled={loading}
            className="btn-arre w-full bg-arre-red hover:bg-arre-red-dark text-white font-display text-xl tracking-widest uppercase py-4 disabled:opacity-50"
            data-testid="auth-submit"
          >
            {loading ? "..." : mode === "login" ? "Ingresar" : "Crear cuenta"}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-white/60">
          {mode === "login" ? "¿No tienes cuenta?" : "¿Ya tienes cuenta?"}{" "}
          <button
            onClick={() => {
              setMode(mode === "login" ? "register" : "login");
              setErr("");
            }}
            className="text-arre-red hover:underline font-bold"
            data-testid="auth-switch"
          >
            {mode === "login" ? "Regístrate" : "Inicia sesión"}
          </button>
        </div>
        <div className="mt-2 text-center text-xs text-white/40">
          O{" "}
          <Link to="/paquetes" className="hover:text-white underline">
            continúa como invitado
          </Link>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="text-xs tracking-[0.25em] uppercase text-white/60">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 bg-arre-black border border-arre-border px-4 py-3 text-white focus:outline-none focus:border-arre-red"
        data-testid={testid}
      />
    </label>
  );
}

export function AccountPage() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [referrals, setReferrals] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    Promise.all([
      api.get("/orders/mine").then((r) => r.data).catch(() => []),
      api.get("/subscriptions/mine").then((r) => r.data).catch(() => []),
      api.get("/referrals/me").then((r) => r.data).catch(() => null),
    ]).then(([o, s, ref]) => {
      setOrders(o);
      setSubs(s);
      setReferrals(ref);
      setLoading(false);
    });
  }, [user, navigate]);

  if (!user) return null;

  return (
    <div className="bg-arre-black min-h-screen py-16 px-6" data-testid="account-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">⟡ Mi cuenta</div>
            <h1 className="font-display text-5xl md:text-7xl tracking-wider uppercase text-white leading-none">
              Hola,<br /><span className="text-arre-red">{user.name.split(" ")[0]}</span>
            </h1>
            <p className="text-white/60 mt-3">{user.email}</p>
          </div>
          <button
            onClick={logout}
            className="btn-arre border border-arre-border hover:border-arre-red text-white font-display text-sm tracking-widest uppercase px-5 py-3 inline-flex items-center gap-2"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" /> Cerrar sesión
          </button>
        </div>

        {/* Quick links */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          <QuickLink to="/referidos" icon={Users} label="Referidos" badge={referrals ? `${referrals.total_conversions} conv` : null} testid="link-referrals" />
          <QuickLink to="/arre-box" icon={Repeat} label="Arre Box" badge={subs.length ? `${subs.filter(s => s.status === "active").length} activa` : null} testid="link-arre-box" />
          <QuickLink to="/favoritos" icon={Heart} label="Favoritos" testid="link-wishlist" />
          <QuickLink to="/rastreo" icon={Package} label="Rastrear" testid="link-tracking" />
        </div>

        {/* Referral credit highlight */}
        {referrals && referrals.credit_balance > 0 && (
          <div className="bg-gradient-to-r from-arre-red to-arre-red-dark p-6 mb-10" data-testid="referral-credit-card">
            <div className="text-[10px] tracking-[0.35em] uppercase text-white/80">Tu saldo Arre</div>
            <div className="font-display text-5xl text-white mt-1">{fmtMXN(referrals.credit_balance)}</div>
            <p className="text-white/80 text-sm mt-2">Se aplica automáticamente en tu próxima compra.</p>
          </div>
        )}

        {/* Subscriptions */}
        {!loading && subs.length > 0 && (
          <section className="mb-12">
            <h2 className="font-display text-3xl tracking-wider uppercase text-white mb-5 flex items-center gap-3">
              <Repeat className="w-6 h-6 text-arre-red" /> Mis BOXes
            </h2>
            <div className="space-y-3" data-testid="account-subs">
              {subs.map((s) => (
                <div key={s.id} className="bg-arre-surface border border-arre-border p-5 flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="font-display text-xl text-white">{s.tier_name}</div>
                    <div className="text-xs text-white/50">{fmtMXN(s.price)}/mes · {s.status}</div>
                  </div>
                  <Link to="/arre-box" className="text-arre-red text-xs tracking-widest uppercase hover:underline">
                    Gestionar →
                  </Link>
                </div>
              ))}
            </div>
          </section>
        )}

        <h2 className="font-display text-3xl tracking-wider uppercase text-white mb-5">Tus pedidos</h2>
        {loading ? (
          <div className="text-white/50">Cargando pedidos...</div>
        ) : orders.length === 0 ? (
          <div className="bg-arre-surface border border-arre-border p-8 text-center">
            <Package className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <p className="font-display text-2xl tracking-wider uppercase text-white mb-2">Aún no tienes pedidos</p>
            <p className="text-white/60 text-sm mb-6">Empieza por un paquetazo.</p>
            <Link
              to="/paquetes"
              className="btn-arre bg-arre-red text-white font-display text-lg tracking-widest uppercase px-6 py-3 inline-block"
            >
              Ver paquetes
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((o) => (
              <div key={o.order_number} className="bg-arre-surface border border-arre-border p-6" data-testid={`order-${o.order_number}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="text-xs tracking-widest uppercase text-white/50">Orden</div>
                    <div className="font-display text-2xl text-white">{o.order_number}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs tracking-widest uppercase text-white/50">Total</div>
                    <div className="font-display text-2xl text-white">{fmtMXN(o.total)}</div>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                  <div className="text-xs tracking-widest uppercase text-arre-red">
                    {o.order_status === "preparing" ? "Preparando" : o.order_status === "shipped" ? "En camino" : o.order_status === "delivered" ? "Entregado" : "Pendiente de pago"}
                  </div>
                  <div className="text-xs text-white/50">{new Date(o.created_at).toLocaleDateString("es-MX")}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function QuickLink({ to, icon: Icon, label, badge, testid }) {
  return (
    <Link
      to={to}
      className="block bg-arre-surface border border-arre-border hover:border-arre-red p-4 transition-colors"
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-5 h-5 text-arre-red" />
        {badge && <span className="text-[10px] tracking-widest uppercase bg-arre-red/20 text-arre-red px-2 py-0.5">{badge}</span>}
      </div>
      <div className="font-display text-sm tracking-widest uppercase text-white">{label}</div>
    </Link>
  );
}
